import { Box, Divider, Skeleton, Button, Image } from "@chakra-ui/react";
import {
  DropdownFilter,
  NoDataPlaceholder,
  QuickActions,
  Table,
  CreatedByFilter,
  TablePageSkeleton,
} from "components/ui";
import {
  useGetAllCollectionsQuery,
  useDeleteCollectionMutation,
  CollectionNode,
  useExportCollectionPdfMutation,
} from "graphql/queries/generated/queries";
import getColumns from "helpers/getColumns";
import { Trans, useTranslation } from "react-i18next";
import { RiAddLine } from "react-icons/ri";
import { createdByFilterFunc } from "components/ui/CreatedByFilter";
import placeholderPNG from "assets/placeholder.png";
import { useNavigate } from "react-router-dom";
import { handleMutation, toast } from "middleware/Toaster";
import downloadFile from "helpers/downloadFile";
import { MGMT_ROUTES } from "routes/constants";

const QUICK_ACTIONS = [
  {
    name: "Add new collection entry",
    href: "new",
    icon: <RiAddLine />,
  },
];
const CUSTOM_FIELDS = [
  "createdBy",
  "totalAssetsNumber",
  "validatedAssetsNumber",
  "createdFrom",
  "actualStatusObjects",
  "createdAt",
  "image",
  "name",
];
const HIDDEN_FIELDS = ["description", "notes"];
const CUSTOM_COLUMNS = [
  {
    Header: "Image",
    footer: "",
    accessor: "image",
    Cell: ({ cell: { value } }: { cell: { value: string } }) =>
      value ? (
        <Button
          style={{ height: "auto", maxWidth: "120px" }}
          variant="unstyled"
          title="Open in new window"
          onClick={() => window.open(value)}
        >
          <img src={value} alt="" />
        </Button>
      ) : (
        <Image src={placeholderPNG} alt="" />
      ),

    disableFilters: true,
  },
  {
    Header: "Name",
    Footer: "",
    accessor: "name",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <div>{value}</div>,
    disableFilters: true,
  },
  {
    Header: "Creation date",
    Footer: "",
    accessor: "createdAt",
    Cell: ({ cell: { value } }: { cell: { value: string } }) =>
      value ? <div>{new Date(value).toLocaleDateString("it-IT")}</div> : "",
    disableFilters: true,
    weight: 0,
  },
  {
    Header: "Total Objects N.",
    Footer: "Total Objects N.",
    accessor: "totalAssetsNumber",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 0,
  },
  {
    Header: "Validated Objects N.",
    Footer: "Validated Objects N.",
    accessor: "validatedAssetsNumber",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    weight: 0,
    disableFilters: true,
  },
  {
    Header: "Created In",
    Footer: "Created In",
    accessor: "createdFrom",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    Filter: DropdownFilter,
    weight: 0,
    disableFilters: false,
  },
  // TODO: REMOVE VISIBLE COLUMN LEAVE ONLY FILTER
  {
    Header: "",
    accessor: "actualStatusObjects",
    Filter: DropdownFilter,
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <span />,
    weight: 0,
  },
  {
    Header: "Created By",
    Footer: "Created By",
    accessor: "createdBy.name",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    Filter: CreatedByFilter,
    filter: createdByFilterFunc,
    weight: 0,
  },
];

const CollectionsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useGetAllCollectionsQuery();
  const [deleteCollection, { data: deleteData, error: deleteError, loading: deleteLoading }] =
    useDeleteCollectionMutation({
      ...handleMutation("Collection deleted!"),
    });
  const [
    exportCollectionPdf,
    {
      data: exportCollectionPdfData,
      error: exportCollectionPdfError,
      loading: exportCollectionPdfLoading,
    },
  ] = useExportCollectionPdfMutation({
    ...handleMutation("Collection exported!"),
  });

  if (loading) return <TablePageSkeleton loading={loading} />;

  if (!data || !data.allCollections || data.allCollections.edges.length === 0)
    return (
      <Box>
        <QuickActions actions={QUICK_ACTIONS} />
        <Divider borderColor="gray.100" />
        <NoDataPlaceholder />
      </Box>
    );

  const {
    allCollections: { edges },
  } = data;

  if (edges[0] === null || !edges[0].node) return null;

  const dataToGetColumns = edges[0].node;
  const columns = getColumns({
    data: dataToGetColumns,
    customFields: CUSTOM_FIELDS,
    customColumns: CUSTOM_COLUMNS,
  });

  const onDelete = async (id: string) => {
    await deleteCollection({
      variables: {
        input: {
          id,
        },
      },
    });

    await refetch();
  };

  const onEdit = ({ row: { original } }: { row: { original: CollectionNode } }) => {
    const { id, createdFrom } = original;
    if (createdFrom === "spin") {
      toast({
        title: t("Collection can't be edited."),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    navigate(`${MGMT_ROUTES.COLLECTIONS}/edit/${id}`);
  };

  const onGeneratePdf = async (collectionId: string) => {
    const { data } = await exportCollectionPdf({
      variables: {
        input: {
          collectionId,
        },
      },
    });

    const {
      exportCollectionPdf: { url },
    } = data;

    await downloadFile(url);
    // window.open(url);
  };

  const BATCH_ACTIONS = [
    {
      name: <Trans>Delete collection(s)</Trans>,
      intent: "danger",
      action: async (selectedRows: { original: CollectionNode }[]) => {
        await Promise.all(
          selectedRows.map(async (row) => {
            const { original } = row;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await onDelete(original.id);

            return null;
          })
        );

        await refetch();
      },
      loading: deleteLoading,
    },
    {
      name: <Trans>Export PDF</Trans>,
      action: async (selectedRows: { original: CollectionNode }[]) => {
        await Promise.all(
          selectedRows.map(async (row) => {
            const { original } = row;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await onGeneratePdf(original.id);
            return null;
          })
        );
      },
      loading: exportCollectionPdfLoading,
    },
  ];

  return (
    <Box>
      <QuickActions actions={QUICK_ACTIONS} />
      <Divider borderColor="gray.100" />
      <Skeleton isLoaded={!loading}>
        <Table
          hiddenColumns={HIDDEN_FIELDS}
          batchActions={BATCH_ACTIONS}
          columns={columns}
          data={data.allCollections.edges.flatMap((edge) => edge?.node)}
          pathname="/mgmt/collections/"
          deleteAction={onDelete}
          editAction={onEdit}
        />
      </Skeleton>
    </Box>
  );
};

export default CollectionsPage;
